import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { authenticateFromToken } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";

    if (!sessionId) {
      setError("Missing Google session — please try signing in again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        authenticateFromToken(data.token, data.user);
        // Clear the fragment before navigating
        window.history.replaceState(null, "", window.location.pathname);
        const dest = data.user.role === "admin" ? "/admin" : "/dashboard";
        toast.success(`Welcome, ${data.user.full_name?.split(" ")[0] || "there"}!`);
        nav(dest, { replace: true, state: { user: data.user } });
      } catch (err) {
        const detail = err?.response?.data?.detail || "Google sign-in failed";
        setError(detail);
        toast.error(detail);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PublicLayout>
      <section className="py-24 gradient-radial-blue">
        <div className="max-w-md mx-auto px-4 text-center">
          {!error ? (
            <>
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#1E3A8A]" />
              <h1 className="mt-6 font-display font-bold text-2xl text-[#0A192F]">Signing you in…</h1>
              <p className="text-sm text-slate-600 mt-2">Finishing your Google sign-in with Way2Efiling.</p>
            </>
          ) : (
            <>
              <h1 className="font-display font-bold text-2xl text-[#0A192F]">Sign-in failed</h1>
              <p className="text-sm text-red-600 mt-3">{error}</p>
              <button
                onClick={() => nav("/login", { replace: true })}
                className="mt-6 inline-flex items-center px-5 py-2.5 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-medium"
                data-testid="auth-callback-retry"
              >
                Back to sign-in
              </button>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
